import html2canvas from "html2canvas";

// A sanitized product name makes a much friendlier default filename than
// a raw product_id — this mirrors what a human would type by hand.
export function slugifyFilename(name: string): string {
    return (
        name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "") || "product"
    );
}

/**
 * Rasterizes a DOM element (a ProductCard, typically) to a canvas at 2x
 * its own on-screen size, so small preview cards still export crisp
 * without needing to render larger in the DOM. Deliberately doesn't pass
 * `allowTaint`: every image this tool draws comes from our own CORS-clean
 * proxy (see GET /products/{id}/card-image), so a taint here means
 * something regressed and should surface as a real, catchable error
 * rather than a silently broken export. Shared by both the download and
 * copy-to-clipboard actions so they can never drift in how they render.
 */
async function renderElementToCanvas(
    element: HTMLElement,
): Promise<HTMLCanvasElement> {
    return html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
    });
}

export async function exportElementAsPng(
    element: HTMLElement,
    filename: string,
): Promise<void> {
    const canvas = await renderElementToCanvas(element);

    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = filename.endsWith(".png") ? filename : `${filename}.png`;
    link.click();
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Failed to create image data"));
        }, "image/png");
    });
}

/**
 * Copies a rendered card straight to the OS clipboard as a PNG, so it can
 * be pasted directly into WhatsApp/Messenger/an email without a
 * download-then-attach round trip. Requires the async Clipboard API's
 * image support (Chromium/Safari; not Firefox as of writing) — callers
 * should catch and show a clear message rather than fail silently.
 */
export async function copyElementToClipboard(
    element: HTMLElement,
): Promise<void> {
    if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
        throw new Error("Copying images isn't supported in this browser");
    }

    const canvas = await renderElementToCanvas(element);
    const blob = await canvasToPngBlob(canvas);
    await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob }),
    ]);
}
